/* Rasterising an SVG in the browser, because the CDN will not do it.
 *
 * ImageEngine's own documentation is explicit: "SVG files are not resized or
 * converted, just optimized and compressed." So an SVG asked for as PNG comes
 * back as an SVG, and /svg-to-png cannot be built out of a directive the way
 * every other conversion here is. It is a canvas job, on the device, and the
 * clip converter is the precedent -- only the derived file is uploaded.
 *
 * WHY <img> AND NOT innerHTML, <object> OR <iframe>. An SVG is XML and may
 * carry <script>. Loaded through an <img> the browser runs no scripts and
 * fetches no external resources at all: that restriction is the security
 * model, not a limitation to work around. It is also strictly safer than
 * today, where a dropped SVG is uploaded to S3 and served through the CDN
 * script tags and all.
 *
 * WHY NOT resvg-wasm, WHICH IS WHAT THE MARKET LEADER USES. Measured against
 * svgtopng.com's own 2,478,606-byte resvg_bg.wasm, with the exact options its
 * worker passes ({fitTo: {mode: 'zoom', value: 2}}):
 *
 *     source            resvg            <img> + canvas
 *     paths only        19,616 dark px   19,635 dark px    identical to 0.1%
 *     with <text>       0 dark px        2,308 dark px     resvg DROPS IT
 *
 * resvg-wasm has no fonts in a browser unless it is handed fontBuffers, and
 * that worker hands it none -- confirmed on the live site, which returned a
 * PNG byte-for-byte identical to a local run of their binary: three shapes and
 * 1280x640 of white where four lines of text should have been. So canvas is
 * not the cheap option here. It is better for text, equal for paths, and costs
 * nothing against 2.5 MB.
 *
 * Untested either way: filters, masks and complex gradients, where resvg may
 * well win. Do not claim blanket parity.
 */
import { MAX_EDGE, MAX_AREA } from "./imageTransform"

/* Something to rasterise when the file says nothing. An SVG carrying only a
 * viewBox has no intrinsic size, and browsers have historically disagreed
 * about what that means -- Firefox rendered 0x0 for years. Deciding it here
 * means one answer rather than the user agent's. */
export const DEFAULT_EDGE = 512

/* What the picture is multiplied by when no width is asked for. The same
 * default svgtopng.com uses, and for the same reason: a vector asked for at
 * its nominal size looks soft on any screen made this decade. */
export const DEFAULT_SCALE = 2

export function looksLikeSvg(file) {
    if (!file) return false
    if ((file.type || "").toLowerCase() === "image/svg+xml") return true
    return /\.svgz?$/i.test(file.name || "")
}

/* The name the PNG is saved under. The extension is REPLACED rather than
 * appended, for the reason gifNameFor() records: "logo.svg.png" is what a tool
 * looks like when nobody thought about it. */
export function pngNameFor(name) {
    const base = String(name || "image").replace(/\.svgz?$/i, "")
    return (base || "image") + ".png"
}

/* How many CSS pixels one of each absolute unit is worth.
 *
 * These are the CSS definitions, which SVG uses: the inch is 96px BY
 * DEFINITION and everything else is derived from it, so none of these is an
 * approximation of a physical measurement -- 1in is exactly 96, 1pt exactly
 * 96/72, and a drawing that says 11.6929in means 1122.5 CSS pixels on every
 * device whatever its real screen is. */
const UNITS = {
    "": 1, px: 1,
    in: 96, cm: 96 / 2.54, mm: 96 / 25.4, q: 96 / 101.6,
    pt: 96 / 72, pc: 16,
}

/* NUMBERS OFF AN SVG ROOT, in the order the spec resolves them.
 *
 * width/height win when they are absolute. A PERCENTAGE IS NOT A SIZE -- it is
 * a share of a viewport this picture does not have -- so "100%" falls through
 * to the viewBox, which is the one thing that always carries a shape.
 *
 * A UNIT IS NOT A SUFFIX TO BE STEPPED OVER, and this cost a real conversion.
 * The line here used to be a bare `parseFloat`, commented "tolerates px/pt/em
 * suffixes" -- which tolerated them by DISCARDING them, correct for `px` and
 * wrong for every other unit there is. Reported against a Visio export
 * declaring `width="11.6929in" height="16.5354in"`: parseFloat gave 11.6929,
 * so the page rasterised an ELEVEN PIXEL WIDE PNG of an A3 diagram and looked
 * for all the world like it had failed to read the file at all.
 *
 * Visio, Illustrator and Inkscape all write physical units by default, so this
 * is the common case rather than an exotic one -- and the viewBox sitting
 * right beside it (`0 0 841.89 1190.55`, the same A3 in points) means the
 * fallback would have been RIGHT. The bug was reading the width at all and
 * then reading it wrongly.
 *
 * em/ex/ch/rem/vw/vh join the percentage instead of being converted: each is a
 * share of something this picture has no access to -- a font size, a viewport
 * -- so like "100%" they are not sizes and fall through to the viewBox. So
 * does any suffix not on the list, because a number whose unit we do not
 * recognise is a number we cannot place. */
/* WHAT GOES BEHIND THE DRAWING.
 *
 * An SVG usually declares no background at all, so the PNG comes out with a
 * transparent one -- which is right for a logo and can be unreadable for a
 * diagram. Reported with a Visio schematic whose lettering is black: opened
 * in anything with a dark background, the labels simply were not there. The
 * picture was perfect and invisible.
 *
 * TRANSPARENT STAYS THE DEFAULT, deliberately. It is what the file itself
 * says, and quietly painting white behind every conversion would break the
 * logo case -- which is the other half of what this page is for -- while
 * looking like it had worked. The answer to "sometimes unreadable" is a
 * choice, not a different silent default.
 *
 * The keys are the ones `colours.*` already names in all ten catalogues, and
 * the hexes are the same values those names carry in lib/meme.js and
 * lib/overlay.js -- one name, one colour, across the whole application.
 * tests/svg_raster_test.mjs reads those two files and fails if they drift. */
export const SVG_BACKGROUNDS = [
    {key: "transparent", hex: ""},
    {key: "white", hex: "#ffffff"},
    {key: "black", hex: "#000000"},
    {key: "sand", hex: "#c8a06a"},
    {key: "lightGreen", hex: "#90ee90"},
    {key: "navy", hex: "#1b2a4a"},
]

/* Empty string, not "transparent" and not null: it is what the canvas already
 * does with no fill, so the default costs no branch anywhere. */
export const DEFAULT_SVG_BACKGROUND = ""

/* THE WIDTHS THE PAGE OFFERS, in pixels, with 0 meaning "twice the drawing's
 * nominal size" -- which is what it has always done and is right for most
 * files.
 *
 * Absolute pixels rather than a multiplier, because a vector has no size to
 * multiply: "2x" means nothing to somebody who needs a 1024px app icon, and
 * the nominal size of an SVG is frequently an accident of whatever drew it.
 *
 * The top of the list is MAX_EDGE. Anything larger is refused by the canvas
 * caps rather than by this list -- targetSize() holds both, so a wide-but-
 * short drawing reaches 4096 and a square one stops at about 3535 where
 * MAX_AREA binds first. Offering a number the canvas would quietly reduce is
 * how a control comes to lie. */
export const SVG_WIDTHS = [0, 512, 1024, 2048, 4096]

export function svgSize(text) {
    const src = String(text || "")
    const root = /<svg\b[^>]*>/i.exec(src)
    const attrs = root ? root[0] : ""

    const attr = (name) => {
        const m = new RegExp(name + '\\s*=\\s*["\']([^"\']*)["\']', "i").exec(attrs)
        if (!m) return 0
        const raw = m[1].trim()
        const parsed = /^([+-]?[\d.]+(?:e[+-]?\d+)?)\s*([a-z%]*)$/i.exec(raw)
        if (!parsed) return 0
        const scale = UNITS[parsed[2].toLowerCase()]
        if (!scale) return 0                      // a share, or a unit we cannot place
        const n = parseFloat(parsed[1]) * scale
        return Number.isFinite(n) && n > 0 ? n : 0
    }

    let w = attr("width")
    let h = attr("height")

    if (!w || !h) {
        const vb = /viewBox\s*=\s*["']([^"']*)["']/i.exec(attrs)
        if (vb) {
            const parts = vb[1].trim().split(/[\s,]+/).map(parseFloat)
            if (parts.length === 4 && parts.every(Number.isFinite) &&
                parts[2] > 0 && parts[3] > 0) {
                // one dimension given, the other implied by the viewBox shape
                if (w && !h) h = w * (parts[3] / parts[2])
                else if (h && !w) w = h * (parts[2] / parts[3])
                else { w = parts[2]; h = parts[3] }
            }
        }
    }

    if (!w || !h) return {width: DEFAULT_EDGE, height: DEFAULT_EDGE, guessed: true}
    return {width: w, height: h, guessed: false}
}

/* How big to actually draw it.
 *
 * `want` is a pixel width when the visitor has asked for one, otherwise the
 * natural size times DEFAULT_SCALE. Then both caps apply -- MAX_EDGE and
 * MAX_AREA are the iOS Safari guard the rest of this application already
 * carries: above roughly 16.7 megapixels it abandons the canvas and hands back
 * a BLANK one rather than an error, which is the worst possible failure for a
 * converter.
 *
 * The aspect ratio is preserved at every step. A vector has no correct size,
 * only a correct SHAPE, and squashing one is not something anybody asked for.
 */
export function targetSize(natural, want = 0, maxEdge = MAX_EDGE, maxArea = MAX_AREA) {
    const nw = natural && natural.width > 0 ? natural.width : DEFAULT_EDGE
    const nh = natural && natural.height > 0 ? natural.height : DEFAULT_EDGE
    const shape = nh / nw

    let w = want > 0 ? want : nw * DEFAULT_SCALE
    let h = w * shape

    const longest = Math.max(w, h)
    if (longest > maxEdge) { const k = maxEdge / longest; w *= k; h *= k }
    if (w * h > maxArea)   { const k = Math.sqrt(maxArea / (w * h)); w *= k; h *= k }

    /* ROUNDING CAN UNDO THE CAP. Scaling to exactly maxArea and then rounding
     * both sides UP puts the product back over it -- measured, a square comes
     * out 3536x3536 = 12,503,296 against a 12,500,000 ceiling. Tiny, and still
     * a cap that does not cap. Floor instead, but only when a cap was the
     * thing that decided the size, so an ordinary 449.5 still rounds to 450. */
    let W = Math.max(1, Math.round(w))
    let H = Math.max(1, Math.round(h))
    if (W > maxEdge || H > maxEdge || W * H > maxArea) {
        W = Math.max(1, Math.floor(w))
        H = Math.max(1, Math.floor(h))
    }
    return {width: W, height: H}
}

/* WHAT WILL NOT SURVIVE THE CONVERSION, so the page can say so first.
 *
 * Both of these are SILENT failures -- the PNG comes back looking fine and
 * something is simply missing from it, which is the shape of bug that reaches
 * a person rather than a test. Same argument as the animated-GIF notice: a
 * NOTICE and not a refusal, because converting a logo whose one text element
 * is decorative is a perfectly reasonable thing to want.
 *
 * Over-detection is deliberately cheap. A <text> inside <defs> that nothing
 * references will never be painted, and warning about it costs a sentence;
 * missing one costs the visitor their words.
 */
export function svgWarnings(text) {
    const src = String(text || "")
    return {
        // <text>, <tspan>, <textPath>. Rendered with the fonts on THIS device,
        // because the <img> sandbox will not fetch a @font-face URL.
        text: /<(text|tspan|textPath)\b/i.test(src),
        // an external picture or stylesheet the sandbox refuses to fetch
        external: /<image\b[^>]*\b(?:xlink:)?href\s*=\s*["']https?:/i.test(src) ||
                  /@font-face[^}]*url\s*\(\s*["']?https?:/i.test(src) ||
                  /<\?xml-stylesheet[^>]*href\s*=\s*["']https?:/i.test(src),
    }
}

/* The browser half. Everything above is pure so tests/svg_raster_test.mjs can
 * drive it in node; this needs a document and is covered by the browser suite.
 */
export async function rasteriseSvg(file, options) {
    const {width = 0, maxEdge = MAX_EDGE, maxArea = MAX_AREA,
           background = DEFAULT_SVG_BACKGROUND} = options || {}

    const source = await file.text()
    const natural = svgSize(source)
    const size = targetSize(natural, width, maxEdge, maxArea)

    /* The ORIGINAL bytes, not a re-serialised copy. Round-tripping an SVG
     * through a parser is a way to lose exactly the constructs that are hard
     * to get right, and nothing here needs to modify the markup. */
    const url = URL.createObjectURL(file)
    try {
        const img = await new Promise((resolve, reject) => {
            const el = new Image()
            el.onload = () => resolve(el)
            // A malformed SVG lands here. It is the only signal there is:
            // <img> reports no parse detail whatsoever.
            el.onerror = () => reject(new Error("unreadable"))
            el.src = url
        })

        const canvas = document.createElement("canvas")
        canvas.width = size.width
        canvas.height = size.height
        const ctx = canvas.getContext("2d")
        /* BEFORE the drawing, so the drawing sits on top of it rather than
         * being covered by it. A canvas starts transparent, so the default
         * needs no fill at all -- and `fillRect` with no colour set would
         * paint black, which is the one wrong answer available here. */
        if (background) {
            ctx.fillStyle = background
            ctx.fillRect(0, 0, size.width, size.height)
        }
        ctx.drawImage(img, 0, 0, size.width, size.height)

        const blob = await new Promise((resolve, reject) => {
            // A blob URL is same-origin and the sandbox fetches nothing, so
            // the canvas stays clean -- verified, getImageData succeeds. The
            // guard is here because a tainted canvas throws rather than
            // returning null, and the message would otherwise be a SecurityError
            // a long way from anything a visitor could act on.
            try {
                canvas.toBlob((b) => b ? resolve(b) : reject(new Error("empty")),
                              "image/png")
            } catch (e) { reject(new Error("tainted")) }
        })

        return {
            file: new File([blob], pngNameFor(file.name), {type: "image/png"}),
            width: size.width,
            height: size.height,
            natural,
            warnings: svgWarnings(source),
        }
    } finally {
        URL.revokeObjectURL(url)
    }
}
